import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  CreditCard,
  Heart,
  Info,
  Loader2,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "../api/config";
import LanguageSupportNotice from "../Components/LanguageSupportNotice";
import { givingFunds, givingMethods, siteMeta } from "../siteData";

const suggestedAmounts = [25, 50, 100, 250, 500, 1000];
const donationOptions = [
  { id: "tithe", label: "Tithe account (አስራት በኩራት)", icon: Heart },
  {
    id: "building",
    label: "Church Building Fund (የቤተክርስቲያን ህንፃ መግዢያ)",
    icon: ShieldCheck,
  },
  { id: "general", label: "Membership or General Offering", icon: Check },
];

async function parseResponse(response) {
  const requestId =
    response.headers.get("x-request-id") || response.headers.get("rndr-id");
  const text = await response.text();

  if (!text) {
    return { payload: {}, requestId };
  }

  try {
    return { payload: JSON.parse(text), requestId };
  } catch {
    return {
      payload: {
        error: "The server returned an unexpected response.",
        rawResponse: text,
      },
      requestId,
    };
  }
}

function buildErrorMessage(payload, requestId, fallback) {
  const base =
    payload && typeof payload === "object" && payload.error
      ? payload.error
      : fallback;

  return requestId ? `${base} Request ID: ${requestId}` : base;
}

function StatusBanner({ status }) {
  if (status === "success") {
    return (
      <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-900">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Donation submitted</p>
            <p className="text-sm mt-1">
              Thank you for your donation. Your payment is being confirmed. If an
              email address was provided and Stripe receipts are enabled, Stripe
              will email your receipt.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status === "cancelled") {
    return (
      <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-900">
        <div className="flex items-start gap-3">
          <XCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Checkout canceled</p>
            <p className="text-sm mt-1">
              Your donation was not completed. You can review the amount and try
              again whenever you are ready.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default function Donations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [amount, setAmount] = useState("50");
  const [donationType, setDonationType] = useState("general");
  const [frequency, setFrequency] = useState("one_time");
  const [donorEmail, setDonorEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const checkoutStatus = searchParams.get("status");

  const selectedFund = useMemo(
    () =>
      donationOptions.find((item) => item.id === donationType) || donationOptions[0],
    [donationType]
  );

  useEffect(() => {
    if (!checkoutStatus) {
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("status");

    const timer = window.setTimeout(() => {
      setSearchParams(nextParams, { replace: true });
    }, 12000);

    return () => window.clearTimeout(timer);
  }, [checkoutStatus, searchParams, setSearchParams]);

  async function handleSubmit(event) {
    event.preventDefault();

    if (status === "loading") {
      return;
    }

    setErrorMessage("");

    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setStatus("error");
      setErrorMessage("Enter a valid donation amount greater than zero.");
      return;
    }

    if (numericAmount > 50000) {
      setStatus("error");
      setErrorMessage("Please enter an amount below $50,000.");
      return;
    }

    const trimmedEmail = donorEmail.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setStatus("error");
      setErrorMessage("Enter a valid email address for the Stripe receipt.");
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: numericAmount,
          donationType: selectedFund.label,
          frequency,
          ...(trimmedEmail ? { donorEmail: trimmedEmail } : {}),
        }),
      });

      const { payload, requestId } = await parseResponse(response);

      if (!response.ok) {
        throw new Error(
          buildErrorMessage(payload, requestId, "Unable to start secure checkout.")
        );
      }

      if (!payload.url) {
        throw new Error(
          buildErrorMessage(
            payload,
            requestId,
            "The checkout session was created without a redirect URL."
          )
        );
      }

      window.location.assign(payload.url);
    } catch (error) {
      setStatus("error");
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Unable to start secure checkout."
      );
      return;
    }

    setStatus("idle");
  }

  return (
    <div className="font-sans-clean min-h-screen bg-[#FDF8F3]">
      <section className="relative py-20 sm:py-24 overflow-hidden bg-[#5b2229]">
        <div className="absolute inset-0 opacity-20 ethiopian-cross" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-serif-elegant text-4xl sm:text-5xl font-bold text-white mb-4">
              Give With Clarity
            </h1>
            <p className="text-white/80 text-lg">
              Support worship, formation, and the long-term health of {siteMeta.shortName}{" "}
              through a secure giving flow.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <LanguageSupportNotice />
        <StatusBanner status={checkoutStatus} />

        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl shadow-sm border border-[#C9A227]/10 p-8"
          >
            <h2 className="font-serif-elegant text-2xl font-bold text-[#722F37] mb-6">
              Secure online giving
            </h2>

            <div className="mb-8">
              <p className="text-sm font-medium text-gray-700 mb-3">Choose a fund</p>
              <div className="grid sm:grid-cols-3 gap-3">
                {donationOptions.map((option) => {
                  const Icon = option.icon;
                  const active = option.id === donationType;

                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setDonationType(option.id)}
                      className={`rounded-2xl border p-4 text-left transition-colors ${
                        active
                          ? "border-[#722F37] bg-[#722F37] text-white"
                          : "border-[#C9A227]/20 bg-[#f7f1e8] text-[#722F37]"
                      }`}
                      aria-pressed={active}
                    >
                      <Icon className="w-5 h-5 mb-3" />
                      <p className="text-sm font-semibold">{option.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-8">
              <p className="text-sm font-medium text-gray-700 mb-3">Suggested amounts</p>
              <div className="flex flex-wrap gap-3">
                {suggestedAmounts.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setAmount(String(value))}
                    className={`px-4 py-2 rounded-full border text-sm font-medium ${
                      Number(amount) === value
                        ? "bg-[#C9A227] text-[#722F37] border-[#C9A227]"
                        : "border-[#C9A227]/30 text-[#722F37]"
                    }`}
                    aria-pressed={Number(amount) === value}
                  >
                    ${value}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Amount
                </span>
                <input
                  id="donation-amount"
                  name="amount"
                  type="number"
                  min="1"
                  max="50000"
                  step="1"
                  inputMode="numeric"
                  autoComplete="off"
                  value={amount}
                  onChange={(event) => setAmount(event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-2">
                  Frequency
                </span>
                <select
                  id="donation-frequency"
                  name="frequency"
                  value={frequency}
                  onChange={(event) => setFrequency(event.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
                >
                  <option value="one_time">One-time gift</option>
                  <option value="recurring">Monthly recurring gift</option>
                </select>
              </label>
            </div>

            <label className="block mb-8">
              <span className="block text-sm font-medium text-gray-700 mb-2">
                Email for Stripe receipt
              </span>
              <input
                id="donor-email"
                name="donorEmail"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={donorEmail}
                onChange={(event) => setDonorEmail(event.target.value)}
                placeholder="name@example.com"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#C9A227]"
              />
              <span className="mt-2 block text-xs text-gray-500">
                Optional. Stripe uses this email for its automatic receipt when
                receipts are enabled.
              </span>
            </label>

            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#722F37] px-6 py-3 text-white font-medium hover:bg-[#5A252C] disabled:opacity-70"
            >
              {status === "loading" ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              {status === "loading"
                ? "Starting secure checkout..."
                : "Continue to secure checkout"}
            </button>

            {errorMessage && (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p>{errorMessage}</p>
                </div>
              </div>
            )}
          </motion.form>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-8 border border-[#C9A227]/10 shadow-sm"
            >
              <h3 className="font-serif-elegant text-xl font-bold text-[#722F37] mb-4">
                What your giving supports
              </h3>
              <div className="space-y-4">
                {givingFunds.map((fund) => (
                  <div key={fund.title} className="rounded-2xl bg-[#f7f1e8] p-4">
                    <p className="font-semibold text-[#722F37]">{fund.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{fund.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-3xl p-8 border border-[#C9A227]/10 shadow-sm"
            >
              <h3 className="font-serif-elegant text-xl font-bold text-[#722F37] mb-4">
                Other ways to give
              </h3>
              <div className="space-y-4 text-sm text-gray-700">
                {givingMethods.map((method) => (
                  <div key={method.title}>
                    <p className="font-medium text-gray-900 mb-1">{method.title}</p>
                    <p>{method.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#f7f1e8] rounded-3xl p-8 border border-[#C9A227]/20"
            >
              <div className="flex items-center gap-3 mb-4">
                <Info className="w-5 h-5 text-[#722F37]" />
                <h3 className="font-serif-elegant text-xl font-bold text-[#722F37]">
                  Giving notes
                </h3>
              </div>
              <ul className="space-y-3 text-sm text-gray-700">
                <li>Online gifts are processed through Stripe checkout.</li>
                <li>Donation receipts are handled by Stripe automatic receipts.</li>
                <li>Recurring gifts create a monthly subscription checkout session.</li>
                <li>Successful and canceled checkouts display a status banner after redirect.</li>
                <li>If checkout fails, the error message includes the backend request ID when available.</li>
                <li>For stewardship questions, contact the parish office at {siteMeta.email}.</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
